import React, { useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import { CtaLinkListProps } from '@/components/organisms/cta-link-list/cta-link-list';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import NumberedSectionsCard from './numbered-sections-card/numbered-sections-card';
import NumberedSectionsImageSlider from './numbered-sections-image-slider/numbered-sections-image-slider';
import styles from './numbered-sections.module.scss';

interface NumberedSection {
  /** The text to show */
  contentHtml: string;
  /** An array of links to show */
  ctaLinks?: CtaLinkListProps['items'];
  /** The headline of the section */
  headline: string;
  /** The id of the section */
  id: string;
  /** The image to show */
  image: Content.Properties.Image | null;
}

export interface NumberedSectionsProps extends ModuleWrapperProps {
  /** The header of the module */
  headline: string;
  /** The main image to show */
  image: Content.Properties.Image | null;
  /** The alignment of the image */
  imageAlignment: 'left' | 'right';
  /** The pre headline of the module */
  preHeadline: string;
  /** The sections to show */
  sections: NumberedSection[];
  /** The sub headline of the module */
  subHeadline: string;
}

const NumberedSections = ({
  headline,
  imageAlignment = 'left',
  image,
  preHeadline,
  sections,
  subHeadline,
  ...rest
}: NumberedSectionsProps) => {
  const isAboveMobile = useWindowSize('tablet');
  const [activeImageIndex, setActiveImageIndex] = useState<number | null>(null);

  const handleViewEnter = (index: number) => {
    if (!isAboveMobile) return;

    setActiveImageIndex(prev => ((prev ?? 0) > index ? prev : index));
  };

  const handleViewExit = () => {
    if (!isAboveMobile) return;

    if (activeImageIndex === null) return;

    const newIndex = activeImageIndex - 1;

    if (newIndex < 0) {
      return setActiveImageIndex(null);
    }

    return setActiveImageIndex(newIndex);
  };

  const renderSections = sections.map((section, sectionIndex) => (
    <NumberedSectionsCard
      key={sectionIndex}
      index={sectionIndex}
      onViewEnter={() => handleViewEnter(sectionIndex)}
      onViewExit={handleViewExit}
      {...section}
    />
  ));

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      <Container>
        <Row>
          <Column
            offset={{
              laptop: imageAlignment === 'right' ? 1 : 0,
            }}
            order={{
              tablet: imageAlignment === 'right' ? 1 : 0,
            }}
            width={{
              laptop: 5,
              tablet: 6,
            }}
          >
            <NumberedSectionsImageSlider
              activeImageIndex={activeImageIndex}
              className={styles.imageSlider}
              images={sections.map(section => section.image)}
              mainImage={image}
            />
          </Column>

          <Column
            offset={{
              laptop: imageAlignment === 'right' ? 0 : 1,
            }}
            width={{
              tablet: 6,
            }}
          >
            <div className={styles.header}>
              <Typography
                className={styles.caption}
                tag="small"
                tagStyle="subHeading"
              >
                {preHeadline}
              </Typography>

              <Typography
                className={styles.headline}
                tag="h2"
                tagStyle="displayMedium"
              >
                {headline}
              </Typography>

              <Typography
                className={styles.subheader}
                tag="h3"
                tagStyle="bodyLargeBold"
              >
                {subHeadline}
              </Typography>
            </div>

            {renderSections}
          </Column>
        </Row>
      </Container>
    </ModuleWrapper>
  );
};

NumberedSections.displayName = 'NumberedSections';

export default NumberedSections;
